import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import HomeIcon from '@mui/icons-material/Home';
import MessageIcon from '@mui/icons-material/Message';
import axios from 'axios';

const CreateInquiry = ({ open, onClose, propertyId, buyerId, propertyTitle }) => {
  const [inquiryText, setInquiryText] = useState('I am interested in this property. Can I schedule a visit?');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async () => {
    if (!inquiryText.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter an inquiry message',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        'https://propertydealstalk.com/api/messaging/createInquiry',
        {
          buyer_id: buyerId,
          property_id: propertyId,
          inquiry_text: inquiryText
        }
      );

      if (response.data.statusCode === 200) {
        setSnackbar({
          open: true,
          message: 'Inquiry sent successfully!',
          severity: 'success'
        });
        setInquiryText('I am interested in this property. Can I schedule a visit?');
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to send inquiry');
      }
    } catch (error) {
      console.error('Error sending inquiry:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send inquiry. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '12px',
            width: '100%',
            maxHeight: '90vh',
            fontFamily: 'Outfit, sans-serif'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          pb: 2,
          background: '#f8faff',
          fontFamily: 'Outfit, sans-serif'
        }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600, 
              color: '#2563EB',
              fontFamily: 'Outfit, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <MessageIcon fontSize="small" /> Contact Property Owner
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 3, fontFamily: 'Outfit, sans-serif' }}>
          <Paper elevation={0} sx={{ 
            p: 2, 
            mb: 3, 
            background: '#f8faff', 
            border: '1px solid #e6effd',
            borderRadius: '10px'
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: 0.5, 
                color: '#2563EB',
                fontFamily: 'Outfit, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <HomeIcon fontSize="small" /> Property
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500,
                fontFamily: 'Outfit, sans-serif'
              }}
            >
              {propertyTitle || 'Luxury Apartment'}
            </Typography>
          </Paper>

          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: 1, 
                color: '#2563EB',
                fontFamily: 'Outfit, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <MessageIcon fontSize="small" /> Your Message
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={inquiryText}
              onChange={(e) => setInquiryText(e.target.value)}
              placeholder="Write your inquiry here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: 'Outfit, sans-serif'
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Outfit, sans-serif'
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontFamily: 'Outfit, sans-serif',
              fontStyle: 'italic'
            }}
          >
            By sending this inquiry, you agree to our terms of service and privacy policy.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ 
          p: 2, 
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          background: '#f8faff'
        }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              width: isMobile ? '100%' : 'auto',
              fontFamily: 'Outfit, sans-serif',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ 
              borderRadius: '8px',
              backgroundColor: '#2563EB',
              '&:hover': {
                backgroundColor: '#1E40AF',
              },
              width: isMobile ? '100%' : 'auto',
              fontFamily: 'Outfit, sans-serif',
              textTransform: 'none',
              fontWeight: 500,
              py: 1
            }}
          >
            {loading ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateInquiry;